import { DateUtils } from '@brunommsilva/react-foundations';
import { DatePicker } from '@brunommsilva/react-foundations';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TEST_IDS } from '../../../config/test-ids';
import { TransactionType } from '../../../models/transactions';
import AccountsDropdown from '../../accounts/accounts-dropdown';
import CategoriesDropdown from '../../categories/categories-dropdown';
import TransactionTypeTabs from '../transaction-type-tabs';

export type FormState = {
  date?: Date;
  type?: TransactionType;
  account?: { id: string; name: string };
  otherAccount?: { id: string; name: string };
  category?: { id: string; name: string; icon?: string };
  amount?: number;
  description?: string;
};

export const INITIAL_FORM_STATE: FormState = {
  date: DateUtils.now(),
  type: TransactionType.DEBIT,
};

interface TransactionFormProps {
  initialValues?: FormState;
  onSubmit: (values: FormState) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitLabel: string;
  title?: string;
  isDialog?: boolean;
}

const TransactionForm = ({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel,
  title,
  isDialog = false,
}: TransactionFormProps) => {
  const { t } = useTranslation();
  const [state, setState] = useState<FormState>(
    () => initialValues || INITIAL_FORM_STATE
  );

  useEffect(() => {
    setState(initialValues || INITIAL_FORM_STATE);
  }, [initialValues]);

  const isFormValid = () => {
    if (state.type === TransactionType.TRANSFER && !state.account) {
      return false;
    }

    return (
      state.date &&
      state.otherAccount &&
      state.category &&
      state.type &&
      state.amount &&
      state.amount > 0
    );
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      onSubmit(state);
    }
  };

  const formContent = (
    <Stack
      sx={{
        gap: 2,
        pt: isDialog ? 1 : 0,
      }}
    >
      <FormControl fullWidth data-testid={TEST_IDS.transactions.typeTabs}>
        <TransactionTypeTabs
          selected={state.type}
          onSelected={(type) => setState({ ...state, type })}
        />
      </FormControl>
      <FormControl fullWidth>
        <DatePicker
          label={t('TRANSACTION_DATE')}
          defaultDate={state.date}
          onPicked={(date?: Date) => setState({ ...state, date })}
        />
      </FormControl>
      <FormControl
        fullWidth
        data-testid={TEST_IDS.transactions.accountDropdown}
      >
        <AccountsDropdown
          label={t('TRANSACTION_ACCOUNT')}
          selected={state.otherAccount}
          onSelected={(account) =>
            setState({ ...state, otherAccount: account })
          }
        />
      </FormControl>
      {state.type === TransactionType.TRANSFER && (
        <FormControl fullWidth>
          <AccountsDropdown
            label={t('TRANSACTION_OTHER_ACCOUNT')}
            selected={state.account}
            onSelected={(account) => setState({ ...state, account: account })}
          />
        </FormControl>
      )}
      <FormControl fullWidth>
        <TextField
          type="number"
          label={t('TRANSACTION_AMOUNT')}
          slotProps={{
            htmlInput: { 'data-testid': TEST_IDS.transactions.amountInput },
          }}
          value={
            state.amount !== undefined && !Number.isNaN(state.amount)
              ? state.amount
              : ''
          }
          onChange={(change: React.ChangeEvent<HTMLInputElement>) => {
            const val = Number.parseFloat(change.target.value);
            setState({
              ...state,
              amount: Number.isNaN(val) ? undefined : val,
            });
          }}
        />
      </FormControl>
      <FormControl
        fullWidth
        data-testid={TEST_IDS.transactions.categoryDropdown}
      >
        <CategoriesDropdown
          label={t('TRANSACTION_CATEGORY')}
          selected={state.category}
          onSelected={(category) => setState({ ...state, category: category })}
        />
      </FormControl>
      <FormControl fullWidth>
        <TextField
          label={t('TRANSACTION_DESCRIPTION')}
          slotProps={{
            htmlInput: {
              'data-testid': TEST_IDS.transactions.descriptionInput,
            },
          }}
          value={state.description || ''}
          onChange={(change: React.ChangeEvent<HTMLInputElement>) => {
            setState({ ...state, description: change.target.value });
          }}
        />
      </FormControl>
      <FormControl>
        <Grid
          container
          sx={{
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          {isDialog && onCancel && (
            <Button
              variant="outlined"
              color="inherit"
              disabled={isSubmitting}
              onClick={onCancel}
            >
              {t('CANCEL')}
            </Button>
          )}
          <Button
            variant="contained"
            data-testid={TEST_IDS.transactions.submitButton}
            disabled={isSubmitting || !isFormValid()}
            onClick={handleSubmit}
          >
            {submitLabel}
          </Button>
        </Grid>
      </FormControl>
    </Stack>
  );

  if (isDialog) {
    return formContent;
  }

  return (
    <Card variant="outlined">
      {title && (
        <CardHeader
          title={
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
          }
        />
      )}
      <CardContent>{formContent}</CardContent>
    </Card>
  );
};

export default TransactionForm;
